import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { api, setToken } from "./lib/api.js";
import { socket } from "./lib/socket.js";
import { useStore } from "./lib/store.js";
import { applySafeArea, initTelegram, inTelegram, startMatchId, tg } from "./lib/telegram.js";
import { forgetMatch, recallMatch, rememberMatch } from "./lib/session.js";
import { isMuted, setMuted, sound, unlockAudio } from "./lib/sound.js";
import { History } from "./screens/History.js";
import { Lobby } from "./screens/Lobby.js";
import { Premium } from "./screens/Premium.js";
import { Stats } from "./screens/Stats.js";
import { Rating } from "./screens/Rating.js";
import { Waiting } from "./screens/Waiting.js";
import { Game } from "./screens/Game.js";

export function App() {
  const {
    screen, socketStatus, latencyMs, error, snapshot,
    setScreen, setMe, setSnapshot, setSocketStatus, setLatency, setError, setConfig,
    setDrawOffer, addChat, setChat, showReaction, clearReaction, enterMatch, setIsAdmin, reset,
  } = useStore();
  const [muted, setMutedState] = useState(isMuted());

  useEffect(() => {
    initTelegram();
    applySafeArea();

    let cancelled = false;

    (async () => {
      if (!inTelegram) {
        setError("Открой приложение через Telegram: t.me/pvp_gamesbot/play");
        return;
      }
      try {
        const [auth, cfg] = await Promise.all([api.auth(tg!.initData), api.config()]);
        if (cancelled) return;

        setToken(auth.token);
        setMe(auth.user);
        setConfig({ games: cfg.games, timeControls: cfg.timeControls });
        // Раздел статистики показываем, только если сервер реально пускает.
        api.adminStats().then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
        socket.connect(auth.token);

        // Пришли по приглашению — сразу в партию. Иначе возвращаемся в ту,
        // что была активна до перезагрузки или сворачивания Telegram.
        const invited =
          startMatchId() ?? (auth.startParam?.startsWith("m_") ? auth.startParam.slice(2) : null);
        const matchId = invited ?? recallMatch();

        if (matchId) {
          rememberMatch(matchId);
          enterMatch(matchId);
          socket.joinMatch(matchId);
        } else {
          setScreen("lobby");
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "не удалось войти");
      }
    })();

    const offStatus = socket.onStatus((s) => {
      setSocketStatus(s);
      setLatency(socket.latencyMs);
    });

    const offMsg = socket.on((msg) => {
      switch (msg.t) {
        case "match":
          if (msg.snapshot.status === "active" && useStore.getState().snapshot?.status === "pending") {
            sound.start();
          }
          setSnapshot(msg.snapshot);
          break;
        case "match:end":
          setSnapshot(msg.snapshot);
          forgetMatch();
          break;
        case "draw:offered":
          setDrawOffer(msg.by);
          break;
        case "draw:declined":
          setDrawOffer(null);
          break;
        case "chat:history":
          setChat(msg.messages);
          break;
        case "chat":
          addChat(msg.message);
          sound.notify();
          break;
        case "reaction":
          showReaction(msg.by, msg.emoji, msg.at);
          sound.notify();
          // Реакция живёт пару секунд и гаснет.
          setTimeout(() => clearReaction(msg.by), 2200);
          break;
        case "error":
          // Ошибки хода не должны выкидывать из партии — только сообщаем.
          if (msg.code === "match_not_found" || msg.code === "match_not_joinable") {
            // Партия исчезла или уже занята — не держим ссылку на неё.
            forgetMatch();
            setError(msg.message);
          } else {
            console.warn("сервер:", msg.code, msg.message);
          }
          break;
        default:
          break;
      }
    });

    // Браузер не даёт создать звук до жеста пользователя. Ловим несколько
    // разных событий: на iOS в webview `pointerdown` доходит не всегда,
    // а «разморозить» контекст надо именно внутри обработчика жеста.
    const unlock = () => unlockAudio();
    for (const ev of ["pointerdown", "touchend", "click"] as const) {
      window.addEventListener(ev, unlock, { passive: true });
    }

    const latency = setInterval(() => setLatency(socket.latencyMs), 2000);

    return () => {
      cancelled = true;
      offStatus();
      offMsg();
      clearInterval(latency);
      for (const ev of ["pointerdown", "touchend", "click"] as const) {
        window.removeEventListener(ev, unlock);
      }
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
        <span className={`conn ${socketStatus}`}>
          <span className="dot" />
          {socketStatus === "online"
            ? latencyMs > 0
              ? `${latencyMs} мс`
              : "на связи"
            : socketStatus === "connecting"
              ? "подключаемся"
              : socketStatus === "offline"
                ? "нет связи"
                : ""}
        </span>

        <span className="row" style={{ gap: 6 }}>
          {screen === "lobby" && (
            <>
              <button className="icon-btn" onClick={() => setScreen("rating")} title="Рейтинг">
                🏆
              </button>
              <button className="icon-btn" onClick={() => setScreen("premium")} title="Подписка">
                ⭐
              </button>
              <button
                className="icon-btn"
                onClick={() => setScreen("history")}
                title="История партий"
              >
                📊
              </button>
              {useStore.getState().isAdmin && (
                <button
                  className="icon-btn"
                  onClick={() => setScreen("stats")}
                  title="Статистика"
                >
                  📈
                </button>
              )}
            </>
          )}
          <button
            className="icon-btn"
            onClick={() => {
              const next = !muted;
              setMuted(next);
              setMutedState(next);
              if (!next) {
                unlockAudio();
                sound.select();
              }
            }}
            title={muted ? "Включить звук" : "Выключить звук"}
          >
            {muted ? "🔇" : "🔊"}
          </button>
        </span>
      </div>

      <div className="screen">
      <AnimatePresence mode="wait">
        {screen === "boot" && (
          <motion.div key="boot" className="center" exit={{ opacity: 0 }}>
            <div className="stack" style={{ alignItems: "center", gap: 14 }}>
              <div className="spinner" />
              <div className="muted small">Загружаем…</div>
            </div>
          </motion.div>
        )}

        {screen === "error" && (
          <motion.div key="error" className="center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="stack" style={{ alignItems: "center", gap: 14, maxWidth: 320 }}>
              <div style={{ fontSize: 34 }}>⚠️</div>
              <div style={{ fontWeight: 700 }}>Не получилось</div>
              <div className="muted small">{error}</div>
              <button
                className="btn"
                onClick={() => {
                  const id = useStore.getState().activeMatchId;
                  if (id) socket.leaveMatch(id);
                  reset();
                }}
              >
                В меню
              </button>
            </div>
          </motion.div>
        )}

        {screen === "lobby" && <motion.div key="lobby">{<Lobby />}</motion.div>}
        {screen === "history" && <motion.div key="history">{<History />}</motion.div>}
        {screen === "premium" && <motion.div key="premium">{<Premium />}</motion.div>}
        {screen === "stats" && <motion.div key="stats">{<Stats />}</motion.div>}
        {screen === "rating" && <motion.div key="rating">{<Rating />}</motion.div>}
        {screen === "waiting" && <motion.div key="waiting">{<Waiting />}</motion.div>}
        {screen === "game" && <motion.div key="game">{<Game />}</motion.div>}
      </AnimatePresence>
      </div>
    </>
  );
}
